import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import OnboardingTabHeader from 'pages/dashboard/components/OnboardingTabHeader';
import { getShowErrors } from 'pages/dashboard/components/Progress/selectors';
import Button, { ButtonSize, ButtonVariant } from 'components/Button';
import PermissionSection from 'components/permission/PermissionSection';
import ErrorMessage from 'components/Inputs/components/ErrorMessage';
import { AddUserCircleIcon, PlusIcon } from 'components/icons';
import { BlobFileTypes, Permissions } from 'entities/dashboard';
import { hasPermission } from 'pages/dashboard/selectors';
import { UploadFile, File } from 'components/uploadFile';
import { useAppDispatch, useAppSelector } from 'hooks';
import Loading from 'components/Loading';

import { addOwner } from './ownershipSlice';
import Owner from './components/Owner';
import TotalBreakdown from './components/TotalBreakdown';
import {
  getOwners,
  getIsLoading,
  getBlobReferences,
  getLoadingProgress,
  getTotalShare,
} from './selectors';
import {
  createOwnerBlobReference,
  deleteOwnerBlobReference,
  downloadOwnerBlobReference,
} from './thunks';

const Ownership = () => {
  const { t } = useTranslation('ownership');
  const dispatch = useAppDispatch();

  const owners = useAppSelector(getOwners);
  const totalShare = useAppSelector(getTotalShare);
  const isLoading = useAppSelector(getIsLoading);
  const loadingProgress = useAppSelector(getLoadingProgress);
  const blobReferences = useAppSelector(getBlobReferences);
  const showErrors: any = useAppSelector(getShowErrors);
  const ownershipPermissions = useAppSelector((state) =>
    hasPermission(state, Permissions.ownership),
  );

  const showError = useMemo(
    () => showErrors.owner && ownershipPermissions.edit,
    [showErrors.owner, ownershipPermissions.edit],
  );

  const hasUnsavedOwner = owners.some(
    (owner) =>
      !owner.businessesOwnersPersonsId && !owner.businessesOwnersBusinessesId,
  );

  const addOwnerDisabled =
    totalShare >= 100 || hasUnsavedOwner || !ownershipPermissions.edit;

  const handleAddOwner = () => {
    dispatch(addOwner());
  };

  const handleDelete = async (id: number) => {
    await dispatch(deleteOwnerBlobReference(id));
  };

  const handleDownload = async (guid: string, fileName: string) => {
    await dispatch(downloadOwnerBlobReference({ guid, fileName }));
  };

  const handleUpload = async (files: FileList) => {
    if (blobReferences.length) {
      await handleDelete(blobReferences[0].businessesOwnersBlobReferencesId);
    }

    dispatch(
      createOwnerBlobReference({
        files,
        typeCode: BlobFileTypes.organization,
      }),
    );
  };

  return (
    <div className="relative">
      {isLoading && <Loading zIndex={20} />}

      <PermissionSection permission={Permissions.ownership} showMessage>
        <>
          <OnboardingTabHeader
            title={t('ownership:title')}
            subTitle={t('ownership:subTitle')}
          />

          <div className="p-6">
            <p className="heading-lg">{t('ownership:breakdownTitle')}</p>

            {owners.length === 0 && (
              <div className="bg-gray-50 rounded-lg p-4 flex items-start mt-5">
                <AddUserCircleIcon className="text-gray-500 mr-3" />
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {t('ownership:breakdownInfoTitle')}
                  </p>
                  <p className="text-sm text-gray-500">
                    {t('ownership:breakdownInfoText')}
                  </p>
                </div>
              </div>
            )}

            {owners.map((owner, index) => (
              <Owner
                key={`${index}-${
                  owner.businessesOwnersPersonsId ||
                  owner.businessesOwnersBusinessesId ||
                  ''
                }`}
                data={owner}
                index={index}
                showErrors={showError}
                permissions={ownershipPermissions}
              />
            ))}

            <PermissionSection
              permission={Permissions.ownership}
              showPopover
              edit
            >
              <div className="flex mt-5">
                <Button
                  variant={ButtonVariant.link}
                  size={ButtonSize.medium}
                  onClick={handleAddOwner}
                  disabled={addOwnerDisabled}
                >
                  <PlusIcon className="mr-2" />
                  {t('ownership:addOwner')}
                </Button>
              </div>
            </PermissionSection>

            <TotalBreakdown />

            <div className="border-b mt-12">
              <p className="heading-lg mb-1">{t('ownership:docTitle')}</p>
              <p className="text-secondary mb-5">
                {t('ownership:docSubTitle')}
              </p>
            </div>

            <UploadFile
              title={t('ownership:docOrganizationTitle')}
              text={t('ownership:docOrganizationSubTitle')}
              className={classNames(
                'mt-5',
                showError &&
                  blobReferences.length === 0 &&
                  'border border-red-600 p-2 rounded-md',
              )}
              onUpload={handleUpload}
              progressPercent={loadingProgress}
              uploadFiles={blobReferences}
              disabled={!ownershipPermissions.edit}
              permission={Permissions.ownership}
            >
              {({ handleSelect }) =>
                blobReferences.map((file) => (
                  <div key={file.id}>
                    <File
                      file={file}
                      onDelete={() => handleDelete(file.id)}
                      onDownload={(name) => handleDownload(file.blobGuid, name)}
                      handleSelect={handleSelect}
                      readOnly={!ownershipPermissions.edit}
                      permission={Permissions.ownership}
                    />
                  </div>
                ))
              }
            </UploadFile>
            {showError && blobReferences.length === 0 && (
              <ErrorMessage
                error={{
                  message: t('ownership:docOrganizationRequired'),
                }}
              />
            )}
          </div>
        </>
      </PermissionSection>
    </div>
  );
};

export default Ownership;
